import { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import Icon from "@mdi/react";
import {
  mdiBellOutline,
  mdiHistory,
  mdiMapMarkerOutline,
  mdiPencilOutline,
  mdiPhoneOutline,
} from "@mdi/js";
import { Button, Chip, Drawer, Spinner, Tabs, TextArea } from "@heroui/react";
import { ROUTES } from "@/constants/routes";
import { helpStore } from "@/stores/HelpStore";
import type {
  HelpOrderDetail,
  HelpOrderStatus,
  HelpPaymentStatus,
  HelpPoint,
} from "../types";
import { extraOnSiteAmount, orderTotal, blankExecutorAssignment } from "../types";
import {
  HELP_PAYMENT_COLOR,
  HELP_PAYMENT_LABEL,
  HELP_PAYMENT_OPTIONS,
  HELP_STATUS_COLOR,
  HELP_STATUS_LABEL,
  HELP_STATUS_OPTIONS,
  formatHelpDate,
  formatHelpExperienceYears,
  formatHelpMoney,
  formatHelpOrderShortId,
  HELP_PROVIDER_LABEL,
} from "../constants";
import "./HelpOrderDrawer.css";
import HelpRouteEditor from "./HelpRouteEditor";
import HelpReceiptEditor from "./HelpReceiptEditor";
import HelpExecutorEditor from "./HelpExecutorEditor";
import HelpCustomerEditor from "./HelpCustomerEditor";
import HelpNotifyModal from "./HelpNotifyModal";
import HelpLatManualFields from "./HelpLatManualFields";
import { HelpChipPickerPopover, HelpEtaChipPopover } from "./HelpChipPickers";

type HelpEditTab = "customer" | "route" | "executor" | "receipt";

function SectionPlaceholder({ children }: { children: string }) {
  return <p className="help-view-placeholder help-view-placeholder--block">{children}</p>;
}

function CustomerSection({
  order,
  onNotify,
}: {
  order: HelpOrderDetail;
  onNotify: () => void;
}) {
  const hasClient = Boolean(order.clientName.trim());
  const hasPhone = Boolean(order.clientPhone.trim());
  const hasVehicle = Boolean(
    order.vehicle.makeModel.trim() || order.vehicle.regNumber.trim(),
  );
  const hasVin = Boolean(order.vehicle.vin?.trim());
  const clientUrl = order.clientUuid
    ? `${ROUTES.CLIENTS}/${order.clientUuid}`
    : null;

  return (
    <section className="help-view-section help-view-section--summary">
      <span className="help-view-section__title">Заказчик</span>
      <div className="help-view-section__content">
        <div className="help-view-customer">
          <div className="help-view-customer__head">
            <div className="help-view-customer__identity">
              {hasClient && clientUrl ? (
                <a
                  href={clientUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="help-view-customer__name help-view-customer__name--link"
                >
                  {order.clientName}
                </a>
              ) : hasClient ? (
                <span className="help-view-customer__name">{order.clientName}</span>
              ) : (
                <span className="help-view-placeholder">Имя не указано</span>
              )}
              {hasPhone ? (
                <a href={`tel:${order.clientPhone}`} className="help-view-call">
                  <Icon path={mdiPhoneOutline} size={0.7} />
                  {order.clientPhone}
                </a>
              ) : (
                <span className="help-view-placeholder">Телефон не указан</span>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="help-view-customer__btn"
              onPress={onNotify}
            >
              <Icon path={mdiBellOutline} size={0.75} />
              Отправить уведомление
            </Button>
          </div>
          <div className="help-view-kv">
            <div>
              <dt>Автомобиль</dt>
              <dd>
                {hasVehicle ? (
                  <>
                    {order.vehicle.makeModel || "—"}
                    {order.vehicle.regNumber ? (
                      <span className="help-view-muted">
                        {" "}
                        · {order.vehicle.regNumber}
                      </span>
                    ) : null}
                  </>
                ) : (
                  <span className="help-view-placeholder">Автомобиль не указан</span>
                )}
              </dd>
            </div>
            <div>
              <dt>VIN</dt>
              <dd>
                {hasVin ? (
                  order.vehicle.vin
                ) : (
                  <span className="help-view-placeholder">Не указан</span>
                )}
              </dd>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function isPointEmpty(point: HelpPoint | null): boolean {
  if (!point) return true;
  return !point.city.trim() && !point.address.trim();
}

function PointRow({
  label,
  point,
}: {
  label: string;
  point: HelpPoint | null;
}) {
  if (!point) {
    return (
      <div className="help-view-point">
        <span className="help-view-point__label">{label}</span>
        <span className="help-view-point__empty">Не требуется</span>
      </div>
    );
  }

  if (isPointEmpty(point)) {
    return (
      <div className="help-view-point">
        <span className="help-view-point__label">{label}</span>
        <span className="help-view-placeholder">Адрес не указан</span>
      </div>
    );
  }

  return (
    <div className="help-view-point">
      <Icon path={mdiMapMarkerOutline} size={0.75} />
      <div className="help-view-point__body">
        <span className="help-view-point__label">{label}</span>
        <span className="help-view-point__address">
          {point.city}, {point.address}
        </span>
        <span className="help-view-point__meta">
          {point.lat.toFixed(5)}, {point.lon.toFixed(5)}
        </span>
        {point.note ? (
          <span className="help-view-point__note">{point.note}</span>
        ) : null}
      </div>
    </div>
  );
}

const HelpOrderDrawer: React.FC = observer(() => {
  const navigate = useNavigate();
  const {
    isDrawerOpen,
    selectedOrder: order,
    isDetailLoading,
    catalog,
    executors,
    commentText,
    isSaving,
    isCommentSubmitting,
    isNotifying,
    isEditing,
    editDraft: orderDraft,
    discardIntent,
  } = helpStore;
  const isLatSending = helpStore.isLatSending;
  const isLatSyncing = helpStore.isLatSyncing;
  const canSendToLat =
    order != null &&
    order.provider === "internal" &&
    (order.status === "awaiting_confirmation" ||
      order.status === "en_route" ||
      order.status === "on_site" ||
      order.status === "in_progress");
  const canSyncFromLat =
    order != null && order.provider === "lat" && Boolean(order.latOrderId);
  const statusOptions =
    order?.status === "awaiting_payment"
      ? (["awaiting_payment", "awaiting_confirmation", "cancelled"] as const)
      : HELP_STATUS_OPTIONS.filter((status) => status !== "awaiting_payment");
  const [isNotifyOpen, setIsNotifyOpen] = useState(false);
  const [editTab, setEditTab] = useState<HelpEditTab>("customer");
  const viewScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isDrawerOpen) {
      setIsNotifyOpen(false);
      setEditTab("customer");
    }
  }, [isDrawerOpen]);

  useEffect(() => {
    if (!isEditing) setEditTab("customer");
  }, [isEditing]);

  useEffect(() => {
    if (!isDrawerOpen || isEditing) return;
    viewScrollRef.current?.scrollTo({ top: 0 });
  }, [isDrawerOpen, isEditing, order?.uuid]);

  const extraOnSite = order == null ? 0 : extraOnSiteAmount(order);

  const handleClose = () => helpStore.requestCloseDrawer();

  const handleStatus = (status: HelpOrderStatus) => {
    void helpStore.updateStatus(status);
  };

  const handlePrepaidStatus = (status: HelpPaymentStatus) => {
    void helpStore.updatePaymentStatus("prepaid", status);
  };

  const handleExtraPaymentStatus = (status: HelpPaymentStatus) => {
    void helpStore.updatePaymentStatus("extra", status);
  };

  const handleExecutorEta = (etaMinutes: number | null) => {
    void helpStore.updateExecutorEta(etaMinutes);
  };

  const handleNotify = async (text: string) => {
    const sent = await helpStore.notifyClient(text);
    if (sent) setIsNotifyOpen(false);
  };

  const handleAddComment = () => {
    void helpStore.addComment();
  };

  const handleOpenClient = () => {
    if (!order?.clientUuid) return;
    if (helpStore.requestOpenClient(order.clientUuid)) {
      navigate(`${ROUTES.CLIENTS}/${order.clientUuid}`);
    }
  };

  const handleConfirmDiscard = () => {
    const clientUuid = helpStore.confirmDiscard();
    if (clientUuid) {
      navigate(`${ROUTES.CLIENTS}/${clientUuid}`);
    }
  };

  return (
    <>
      <Drawer.Backdrop
        isOpen={isDrawerOpen}
        onOpenChange={(open) => {
          if (!open) handleClose();
        }}
        className="help-view-backdrop"
      >
        <Drawer.Content placement="right" className="help-view-content help-order-drawer">
          <Drawer.Dialog className="help-view-dialog" data-testid="help-order-drawer">
            <Drawer.CloseTrigger data-testid="help-order-drawer-close" />
            <Drawer.Header className="help-view-header">
              {order ? (
                <>
                  <div className="help-view-header__top">
                    <Drawer.Heading>{order.serviceTitle || "Заявка"}</Drawer.Heading>
                    <div className="help-view-header__actions">
                      {!isEditing ? (
                        <>
                          <div className="help-view-header__status">
                            <HelpChipPickerPopover
                              ariaLabel="Изменить статус заявки"
                              value={order.status}
                              placement="bottom end"
                              options={statusOptions.map((status) => ({
                                value: status,
                                label: HELP_STATUS_LABEL[status],
                                color: HELP_STATUS_COLOR[status],
                              }))}
                              onChange={(status) => handleStatus(status)}
                            >
                              <Chip
                                size="sm"
                                color={HELP_STATUS_COLOR[order.status]}
                                variant="soft"
                              >
                                {HELP_STATUS_LABEL[order.status]}
                              </Chip>
                            </HelpChipPickerPopover>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onPress={() => helpStore.enterEdit()}
                          >
                            <Icon path={mdiPencilOutline} size={0.75} />
                            Редактировать
                          </Button>
                          {canSendToLat ? (
                            <Button
                              variant="primary"
                              size="sm"
                              isDisabled={isLatSending}
                              onPress={() => void helpStore.sendToLat()}
                            >
                              {isLatSending ? "Отправка…" : "Отправить в ЛАТ"}
                            </Button>
                          ) : null}
                          {canSyncFromLat ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              isDisabled={isLatSyncing}
                              onPress={() => void helpStore.syncFromLat()}
                            >
                              {isLatSyncing ? "Синхронизация…" : "Синхронизировать с ЛАТ"}
                            </Button>
                          ) : null}
                        </>
                      ) : null}
                    </div>
                  </div>

                  <p className="help-view-subtitle" title={order.uuid}>
                    {formatHelpDate(order.createdAt)} · {formatHelpOrderShortId(order.uuid)}
                  </p>

                  {order.comment ? (
                    <p className="help-view-comment">{order.comment}</p>
                  ) : null}
                </>
              ) : (
                <Drawer.Heading>Заявка</Drawer.Heading>
              )}
            </Drawer.Header>

            <Drawer.Body
              className={
                isEditing
                  ? "help-view-body help-view-body--edit"
                  : "help-view-body"
              }
            >
              {isDetailLoading ? (
                <div className="help-view-loading">
                  <Spinner />
                </div>
              ) : order && isEditing && orderDraft ? (
                <div className="help-order-edit">
                  <Tabs
                    selectedKey={editTab}
                    onSelectionChange={(key) => {
                      if (!key) return;
                      setEditTab(String(key) as HelpEditTab);
                    }}
                    className="help-order-edit__tabs"
                  >
                    <Tabs.ListContainer>
                      <Tabs.List aria-label="Разделы заявки">
                        <Tabs.Tab id="customer">
                          Заказчик
                          <Tabs.Indicator />
                        </Tabs.Tab>
                        <Tabs.Tab id="route">
                          Маршрут
                          <Tabs.Indicator />
                        </Tabs.Tab>
                        <Tabs.Tab id="executor">
                          Исполнитель
                          <Tabs.Indicator />
                        </Tabs.Tab>
                        <Tabs.Tab id="receipt">
                          Чек и оплата
                          <Tabs.Indicator />
                        </Tabs.Tab>
                      </Tabs.List>
                    </Tabs.ListContainer>

                    <Tabs.Panel id="customer" className="help-order-edit__panel">
                      <div className="help-order-edit__scroll">
                      <HelpCustomerEditor
                        customer={orderDraft.customer}
                        onChange={(customer) =>
                          helpStore.patchEditDraft({ customer })
                        }
                        onOpenClient={handleOpenClient}
                      />
                      </div>
                    </Tabs.Panel>

                    <Tabs.Panel id="route" className="help-order-edit__panel">
                      <div className="help-order-edit__scroll">
                      <HelpRouteEditor
                        embedded
                        mapActive={editTab === "route"}
                        pickup={orderDraft.pickup}
                        dropoff={orderDraft.dropoff}
                        onPickupChange={(pickup) =>
                          helpStore.patchEditDraft({ pickup })
                        }
                        onDropoffChange={(dropoff) =>
                          helpStore.patchEditDraft({ dropoff })
                        }
                      />
                      </div>
                    </Tabs.Panel>

                    <Tabs.Panel id="executor" className="help-order-edit__panel">
                      <div className="help-order-edit__scroll">
                      <HelpExecutorEditor
                        embedded
                        executor={orderDraft.executor ?? blankExecutorAssignment()}
                        roster={executors}
                        onChange={(executor) =>
                          helpStore.patchEditDraft({ executor })
                        }
                        onClear={() =>
                          helpStore.patchEditDraft({
                            executor: blankExecutorAssignment(),
                          })
                        }
                      />
                      </div>
                    </Tabs.Panel>

                    <Tabs.Panel id="receipt" className="help-order-edit__panel">
                      <div className="help-order-edit__scroll">
                      <HelpReceiptEditor
                        embedded
                        items={orderDraft.items}
                        catalog={catalog}
                        orderKind={order.kind}
                        prepaidAmount={orderDraft.prepaidAmount}
                        prepaidStatus={orderDraft.prepaidStatus}
                        extraPaymentStatus={orderDraft.extraPaymentStatus}
                        onItemsChange={(items) => helpStore.setEditItems(items)}
                        onPrepaidChange={(prepaidAmount) =>
                          helpStore.patchEditDraft({ prepaidAmount })
                        }
                        onPrepaidStatusChange={(prepaidStatus) =>
                          helpStore.patchEditDraft({ prepaidStatus })
                        }
                        onExtraStatusChange={(extraPaymentStatus) =>
                          helpStore.patchEditDraft({ extraPaymentStatus })
                        }
                      />
                      </div>
                    </Tabs.Panel>
                  </Tabs>
                </div>
              ) : order ? (
                <div ref={viewScrollRef} className="help-view-scroll">
                  <div className="help-view-grid-row">
                    <CustomerSection
                      order={order}
                      onNotify={() => setIsNotifyOpen(true)}
                    />

                    <section className="help-view-section help-view-section--summary">
                    <div className="help-view-section__head">
                      <span className="help-view-section__title">Исполнитель</span>
                    </div>
                    <div className="help-view-section__content">
                      {order.executor ? (
                        <div className="help-view-executor">
                          <div className="help-view-executor__head">
                            <strong>{order.executor.name}</strong>
                            <HelpEtaChipPopover
                              value={order.executor.etaMinutes}
                              onSave={handleExecutorEta}
                            />
                          </div>
                          {order.executor.source === "custom" ? (
                            <Chip size="sm" variant="soft">
                              Вручную
                            </Chip>
                          ) : null}
                          <div className="help-view-executor__meta">
                            {order.executor.vehicleModel || (
                              <span className="help-view-placeholder">Авто не указано</span>
                            )}
                            {order.executor.vehiclePlate ? (
                              <span className="help-view-plate">
                                {order.executor.vehiclePlate}
                              </span>
                            ) : null}
                          </div>
                          <div className="help-view-executor__meta">
                            {order.executor.experienceYears > 0 ||
                            order.executor.company ? (
                              <>
                                Стаж {formatHelpExperienceYears(order.executor.experienceYears)}
                                {order.executor.company
                                  ? ` · ${order.executor.company}`
                                  : ""}
                              </>
                            ) : (
                              <span className="help-view-placeholder">
                                Компания и стаж не указаны
                              </span>
                            )}
                          </div>
                          {order.executor.phone ? (
                            <a
                              href={`tel:${order.executor.phone}`}
                              className="help-view-call"
                            >
                              <Icon path={mdiPhoneOutline} size={0.7} />
                              {order.executor.phone}
                            </a>
                          ) : (
                            <span className="help-view-placeholder">Телефон не указан</span>
                          )}
                          {order.executor.lat != null && order.executor.lon != null ? (
                            <div className="help-view-executor__geo">
                              <span className="help-view-executor__meta">
                                {order.executor.lat.toFixed(5)}, {order.executor.lon.toFixed(5)}
                              </span>
                            </div>
                          ) : (
                            <span className="help-view-placeholder">
                              Геопозиция не указана
                            </span>
                          )}
                        </div>
                      ) : (
                        <SectionPlaceholder>Исполнитель не назначен</SectionPlaceholder>
                      )}
                    </div>
                  </section>
                  </div>

                  <section className="help-view-section help-view-section--lat">
                    <div className="help-view-section__head">
                      <span className="help-view-section__title">ЛАТ</span>
                      <Chip
                        size="sm"
                        variant="soft"
                        color={order.provider === "lat" ? "accent" : "default"}
                      >
                        {HELP_PROVIDER_LABEL[order.provider]}
                      </Chip>
                    </div>
                    <div className="help-view-section__content">
                      <HelpLatManualFields order={order} />
                      {order.provider === "lat" ? (
                        <div className="help-view-kv">
                          <div>
                            <dt>Контур</dt>
                            <dd>{order.latTest ? "Тест" : "Прод"}</dd>
                          </div>
                          {order.latLastError ? (
                            <div>
                              <dt>Последняя ошибка</dt>
                              <dd className="help-view-lat-error">
                                {order.latLastError}
                              </dd>
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </section>

                  <div className="help-view-grid-row">
                  <section className="help-view-section help-view-section--route">
                    <div className="help-view-section__head">
                      <span className="help-view-section__title">Маршрут</span>
                    </div>
                    <div className="help-view-section__content">
                      {isPointEmpty(order.pickup) && !order.dropoff ? (
                        <SectionPlaceholder>Маршрут не указан</SectionPlaceholder>
                      ) : (
                        <div className="help-view-points">
                          <PointRow label="Забрать" point={order.pickup} />
                          <PointRow label="Отвезти" point={order.dropoff} />
                        </div>
                      )}
                    </div>
                  </section>

                  <section className="help-view-section help-view-section--receipt">
                    <div className="help-view-section__head">
                      <span className="help-view-section__title">Чек и оплата</span>
                    </div>
                    <div className="help-view-section__content help-view-receipt">
                      {order.items.length === 0 ? (
                        <SectionPlaceholder>Позиции чека не добавлены</SectionPlaceholder>
                      ) : (
                        <>
                          <ul className="help-receipt-preview">
                            {order.items.map((item) => (
                              <li key={item.id}>
                                <span>
                                  {item.title}
                                  {item.quantity > 1 ? ` × ${item.quantity}` : ""}
                                </span>
                                <span>
                                  {formatHelpMoney(item.quantity * item.unitPrice)}
                                </span>
                              </li>
                            ))}
                          </ul>
                          <div className="help-receipt-totals">
                            <div className="help-receipt-totals__body">
                              <div className="help-receipt-totals__line help-receipt-totals__line--total">
                                <span className="help-receipt-totals__label">Итого</span>
                              </div>
                              <div className="help-receipt-totals__line">
                                <span className="help-receipt-totals__label">Предоплата</span>
                                <HelpChipPickerPopover
                                  ariaLabel="Изменить статус предоплаты"
                                  value={order.prepaidStatus}
                                  placement="bottom end"
                                  options={HELP_PAYMENT_OPTIONS.map((status) => ({
                                    value: status,
                                    label: HELP_PAYMENT_LABEL[status],
                                    color: HELP_PAYMENT_COLOR[status],
                                  }))}
                                  onChange={handlePrepaidStatus}
                                >
                                  <Chip
                                    size="sm"
                                    variant="soft"
                                    color={HELP_PAYMENT_COLOR[order.prepaidStatus]}
                                  >
                                    {HELP_PAYMENT_LABEL[order.prepaidStatus]}
                                  </Chip>
                                </HelpChipPickerPopover>
                              </div>
                              <div
                                className="help-receipt-totals__divider"
                                aria-hidden="true"
                              />
                              <div className="help-receipt-totals__line help-receipt-totals__line--result">
                                <span className="help-receipt-totals__label">Доплата</span>
                                {extraOnSite === 0 ? (
                                  <Chip size="sm" variant="soft">
                                    Не требуется
                                  </Chip>
                                ) : (
                                  <HelpChipPickerPopover
                                    ariaLabel="Изменить статус доплаты"
                                    value={order.extraPaymentStatus}
                                    placement="bottom end"
                                    options={HELP_PAYMENT_OPTIONS.map((status) => ({
                                      value: status,
                                      label: HELP_PAYMENT_LABEL[status],
                                      color: HELP_PAYMENT_COLOR[status],
                                    }))}
                                    onChange={handleExtraPaymentStatus}
                                  >
                                    <Chip
                                      size="sm"
                                      variant="soft"
                                      color={HELP_PAYMENT_COLOR[order.extraPaymentStatus]}
                                    >
                                      {HELP_PAYMENT_LABEL[order.extraPaymentStatus]}
                                    </Chip>
                                  </HelpChipPickerPopover>
                                )}
                              </div>

                              <div className="help-receipt-totals__values-stack">
                                <span className="help-receipt-totals__minus" aria-hidden="true">
                                  −
                                </span>
                                <span className="help-receipt-totals__num help-receipt-totals__num--total">
                                  {formatHelpMoney(orderTotal(order))}
                                </span>
                                <span className="help-receipt-totals__num">
                                  {formatHelpMoney(order.prepaidAmount)}
                                </span>
                              </div>
                              <span className="help-receipt-totals__num help-receipt-totals__num--result help-receipt-totals__value-result">
                                {formatHelpMoney(extraOnSite)}
                              </span>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </section>
                  </div>

                  <div className="help-view-history-area">
                    <section className="help-view-section help-view-section--timeline">
                      <div className="help-view-section__head">
                        <div className="help-view-section__head-title">
                          <Icon path={mdiHistory} size={0.75} />
                          <span className="help-view-section__title">История</span>
                        </div>
                      </div>
                      {order.events.length === 0 ? (
                        <p className="help-view-empty">Пока нет событий</p>
                      ) : (
                        <ul className="help-view-timeline">
                          {order.events.map((event) => (
                            <li
                              key={event.id}
                              className={
                                event.type === "comment" || event.title === "Комментарий"
                                  ? "help-view-timeline__item--comment"
                                  : undefined
                              }
                            >
                              <div className="help-view-timeline__marker" />
                              <div className="help-view-timeline__content">
                                <div className="help-view-timeline__meta">
                                  <span>{event.title}</span>
                                  <span title={event.at}>{formatHelpDate(event.at)}</span>
                                </div>
                                {event.detail ? <p>{event.detail}</p> : null}
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </section>
                    <div className="help-view-comment-form">
                      <div className="help-view-comment-input">
                        <TextArea
                          fullWidth
                          rows={2}
                          value={commentText}
                          onChange={(e) => helpStore.setCommentText(e.target.value)}
                          placeholder="Добавить комментарий к заявке..."
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                              e.preventDefault();
                              handleAddComment();
                            }
                          }}
                        />
                        <Button
                          className="help-view-comment-input__submit"
                          variant="primary"
                          size="sm"
                          isDisabled={!commentText.trim() || isCommentSubmitting}
                          onPress={handleAddComment}
                        >
                          Добавить
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </Drawer.Body>

            {discardIntent != null ? (
              <Drawer.Footer className="help-order-edit__footer help-order-edit__footer--unsaved">
                <p className="help-unsaved-banner__text">
                  {discardIntent === "close"
                    ? "Есть несохранённые изменения. Закрыть карточку без сохранения?"
                    : discardIntent === "open_client"
                      ? "Есть несохранённые изменения. Перейти к карточке клиента без сохранения?"
                      : "Есть несохранённые изменения. Выйти из редактирования без сохранения?"}
                </p>
                <Button variant="tertiary" onPress={() => helpStore.dismissDiscard()}>
                  Остаться
                </Button>
                <Button variant="secondary" onPress={handleConfirmDiscard}>
                  Без сохранения
                </Button>
              </Drawer.Footer>
            ) : isEditing ? (
              <Drawer.Footer className="help-order-edit__footer">
                <Button
                  variant="tertiary"
                  onPress={() => helpStore.requestCancelEdit()}
                >
                  Отмена
                </Button>
                <Button
                  variant="primary"
                  isDisabled={isSaving}
                  onPress={() => void helpStore.saveEdit()}
                >
                  {isSaving ? "Сохранение..." : "Сохранить изменения"}
                </Button>
              </Drawer.Footer>
            ) : null}
          </Drawer.Dialog>
        </Drawer.Content>
      </Drawer.Backdrop>
      <HelpNotifyModal
        isOpen={isNotifyOpen && order != null}
        order={order}
        onClose={() => setIsNotifyOpen(false)}
        onSend={(text) => void handleNotify(text)}
        isSending={isNotifying}
      />
    </>
  );
});

HelpOrderDrawer.displayName = "HelpOrderDrawer";
export default HelpOrderDrawer;
